// Baselayer Results Explorer — small shared helpers for the tab components:
// locating an add-on row by type, a defensive date formatter for UNVERIFIED
// Baselayer date strings, and shared chip / definition-list primitives.

import * as React from "react";
import { formatCalendarDate } from "@/design-system/lib/format";
import type { BackgroundCheckAddOnDto } from "@/api/backgroundChecks/addOnsApi";

/** The newest (non-superseded) add-on of a given type, preferring a refresh. */
export function findAddOn(
  addOns: BackgroundCheckAddOnDto[] | undefined,
  ...types: string[]
): BackgroundCheckAddOnDto | undefined {
  if (!addOns) return undefined;
  const matches = addOns.filter((a) => types.includes(a.addOnType));
  if (matches.length === 0) return undefined;
  // Latest order wins (refresh supersedes the original).
  return [...matches].sort(
    (a, b) => new Date(b.orderedAt).getTime() - new Date(a.orderedAt).getTime(),
  )[0];
}

export function isCompleted(addOn: BackgroundCheckAddOnDto | undefined): boolean {
  return addOn?.status === "Completed";
}

/**
 * Formats a free-form Baselayer date string; falls back to the raw string.
 *
 * WALL-CALENDAR, not an instant — and that is why this renders through
 * `formatCalendarDate` (which pins `timeZone: "UTC"`) rather than taking the
 * viewer's zone. Every consumer is a public-record calendar day, not a moment:
 * incorporation / formation date (DetailsTab), domain created (WebsiteTab),
 * exclusion-list date, docket `dateFiled` (Litigations, Bankruptcies), lien
 * `filingDate` / `lapseDate`, SoS `issueDate`. A lien was filed ON a day; it
 * does not move because the reader is in Tokyo.
 *
 * The bug this closes is a real off-by-one-day, not a cosmetic one: Baselayer
 * returns these mostly as bare `YYYY-MM-DD`, which `new Date()` parses as UTC
 * MIDNIGHT, so the old bare `toLocaleDateString("en-US", …)` — locale pinned,
 * zone NOT — rendered it in the browser's zone and every negative-offset
 * viewer (Phoenix included) read the PREVIOUS day. Two machines also disagreed
 * with each other, and both disagreed with every other date in the product.
 */
export function formatLooseDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return formatCalendarDate(d);
}

/** A small token chip used for sources / SoS state lists. */
export function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-pill border border-border-light bg-white-warm px-space-2 py-[2px] text-caption text-gray-mid">
      {children}
    </span>
  );
}

export function Chips({ values }: { values: (string | null | undefined)[] }) {
  const items = values.filter((v): v is string => !!v);
  if (items.length === 0) return <span className="text-gray-mid">—</span>;
  return (
    <div className="flex flex-wrap gap-space-1">
      {items.map((v, i) => (
        <Chip key={`${v}-${i}`}>{v}</Chip>
      ))}
    </div>
  );
}

/** A two-column definition list for the Details-style tabs. */
export function DefinitionList({
  items,
}: {
  items: { label: string; value: React.ReactNode }[];
}) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-space-3 m-0">
      {items.map((item) => (
        <div key={item.label} className="flex flex-col gap-[2px]">
          <dt className="font-display text-label font-semibold uppercase tracking-label text-gray-mid">
            {item.label}
          </dt>
          <dd className="m-0 text-small text-onyx break-words">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}
